var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AccessStrategies, makeDocUrl, makePublicDocUrl } from "@cloudy/utils/common";
import { ChevronDownIcon, GlobeIcon, LockIcon, PlusIcon, UsersIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { Button } from "src/components/Button";
import { CopyButton } from "src/components/CopyButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "src/components/Dialog";
import { Input } from "src/components/Input";
import { SelectDropdown } from "src/components/SelectDropdown";
import { Tooltip, TooltipContent, TooltipTrigger } from "src/components/Tooltip";
import { Avatar } from "src/components/users/Avatar";
import { useUser } from "src/stores/user";
import { useWorkspace } from "src/stores/workspace";
import { useProject } from "src/views/projects/ProjectContext";
import { useDocumentContext } from "../DocumentContext";
import { useAddDocumentUser, useDocumentAccessControl, useRemoveDocumentUser } from "../accessControl";
import { useEditDocument } from "../editor/hooks";
import { useThought } from "../editor/hooks";
var makeAccessStrategyInfo = function (accessStrategy) {
    switch (accessStrategy) {
        case AccessStrategies.PUBLIC:
            return { label: "Public", icon: _jsx(GlobeIcon, { className: "size-4" }) };
        case AccessStrategies.PRIVATE:
            return { label: "Invite only", icon: _jsx(LockIcon, { className: "size-4" }) };
        case AccessStrategies.WORKSPACE:
            return { label: "Workspace", icon: _jsx(UsersIcon, { className: "size-4" }) };
    }
};
export var ShareDialogTrigger = function (_a) {
    var accessStrategy = _a.accessStrategy;
    var _b = makeAccessStrategyInfo(accessStrategy !== null && accessStrategy !== void 0 ? accessStrategy : AccessStrategies.PRIVATE), label = _b.label, icon = _b.icon;
    return (_jsxs(Button, { variant: "outline", size: "sm", className: "pr-1", children: [icon, _jsx("span", { children: label }), _jsx(ChevronDownIcon, { className: "size-4" })] }));
};
export var ShareDialog = function (_a) {
    var trigger = _a.trigger;
    var workspace = useWorkspace();
    var project = useProject();
    var user = useUser();
    var documentId = useDocumentContext().documentId;
    var thought = useThought(documentId).data;
    var _b = useDocumentAccessControl(documentId), accessStrategy = _b.accessStrategy, users = _b.users;
    var editDocumentMutation = useEditDocument(documentId);
    var _c = useState(false), isOpen = _c[0], setIsOpen = _c[1];
    var _d = useState(""), email = _d[0], setEmail = _d[1];
    var addUserMutation = useAddDocumentUser(documentId);
    var removeUserMutation = useRemoveDocumentUser(documentId);
    var shareLink = makeDocUrl(process.env.REACT_APP_PUBLIC_URL, {
        workspaceSlug: workspace.slug,
        documentId: documentId,
        projectSlug: project === null || project === void 0 ? void 0 : project.slug,
    });
    var publicLink = makePublicDocUrl(process.env.REACT_APP_PAGES_PUBLIC_URL, {
        workspaceSlug: workspace.slug,
        documentId: documentId,
        projectSlug: project === null || project === void 0 ? void 0 : project.slug,
    });
    var isAuthor = (thought === null || thought === void 0 ? void 0 : thought.author_id) === user.id;
    var accessControlOptions = [
        {
            value: AccessStrategies.PRIVATE,
            label: isAuthor ? "Invite only" : "Invite only (only the author can set it to this)",
            icon: _jsx(LockIcon, { className: "mr-2 size-4" }),
            disabled: !isAuthor,
        },
        { value: AccessStrategies.WORKSPACE, label: "Anyone in the workspace", icon: _jsx(UsersIcon, { className: "mr-2 size-4" }) },
        {
            value: AccessStrategies.PUBLIC,
            label: "Public",
            icon: _jsx(GlobeIcon, { className: "mr-2 size-4" }),
        },
    ];
    var handleUpdateAccessStrategy = function (accessStrategy) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    // Prevent non-authors from setting private access
                    if (accessStrategy === AccessStrategies.PRIVATE && !isAuthor) {
                        return [2 /*return*/];
                    }
                    return [4 /*yield*/, editDocumentMutation.mutateAsync({ accessStrategy: accessStrategy, ts: new Date() })];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); };
    var currentAccessStrategy = accessStrategy !== null && accessStrategy !== void 0 ? accessStrategy : AccessStrategies.PRIVATE;
    var handleInviteUser = function () { return __awaiter(void 0, void 0, void 0, function () {
        var error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, addUserMutation.mutateAsync(email)];
                case 1:
                    _a.sent();
                    setEmail(""); // Clear input on success
                    return [3 /*break*/, 3];
                case 2:
                    error_1 = _a.sent();
                    // You might want to show an error toast here
                    console.error("Failed to invite user:", error_1);
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); };
    var handleRemoveUser = function (userId) { return __awaiter(void 0, void 0, void 0, function () {
        var error_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, removeUserMutation.mutateAsync(userId)];
                case 1:
                    _a.sent();
                    return [3 /*break*/, 3];
                case 2:
                    error_2 = _a.sent();
                    console.error("Failed to remove user:", error_2);
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); };
    return (_jsxs(Dialog, { open: isOpen, onOpenChange: setIsOpen, children: [_jsxs(Tooltip, { durationPreset: "short", children: [_jsx(TooltipTrigger, { asChild: true, children: _jsx(DialogTrigger, { children: trigger !== null && trigger !== void 0 ? trigger : _jsx(ShareDialogTrigger, { accessStrategy: currentAccessStrategy }) }) }), _jsx(TooltipContent, { children: _jsx("span", { children: "Control sharing and who has access" }) })] }), _jsxs(DialogContent, { size: "lg", children: [_jsx(DialogHeader, { children: _jsx(DialogTitle, { children: "Document Sharing" }) }), _jsxs("div", { className: "space-y-4", children: [_jsxs("div", { children: [_jsx("label", { htmlFor: "share-link", className: "text-sm", children: "Internal link" }), _jsxs("div", { className: "mt-1 flex gap-2", children: [_jsx(Input, { id: "share-link", value: shareLink, readOnly: true, className: "flex-grow" }), _jsx(CopyButton, { textToCopy: shareLink })] })] }), currentAccessStrategy === AccessStrategies.PUBLIC && (_jsxs("div", { children: [_jsx("label", { htmlFor: "public-link", className: "text-sm", children: "Public link" }), _jsxs("div", { className: "mt-1 flex gap-2", children: [_jsx(Input, { id: "public-link", value: publicLink, readOnly: true, className: "flex-grow" }), _jsx(CopyButton, { textToCopy: publicLink })] })] })), _jsxs("div", { children: [_jsx("label", { htmlFor: "access-control", className: "text-sm", children: "Access Control" }), _jsx(SelectDropdown, { options: accessControlOptions, value: currentAccessStrategy, onChange: function (value) { return handleUpdateAccessStrategy(value); }, placeholder: "Select access control", className: "mt-1 w-full" })] }), _jsxs("div", { children: [_jsx("label", { className: "text-sm", children: "People with access" }), _jsxs("div", { className: "mt-1 flex gap-2", children: [_jsx(Input, { id: "invite-users", placeholder: "Enter email address", className: "flex-grow", value: email, onChange: function (e) { return setEmail(e.target.value); }, onKeyDown: function (e) {
                                                    if (e.key === "Enter" && email) {
                                                        handleInviteUser();
                                                    }
                                                } }), _jsx(Button, { variant: "outline", onClick: handleInviteUser, disabled: !email || addUserMutation.isPending, children: addUserMutation.isPending ? (_jsx("span", { className: "animate-spin", children: "..." })) : (_jsxs(_Fragment, { children: [_jsx(PlusIcon, { className: "size-4" }), "Invite"] })) })] }), _jsx("div", { className: "mt-2 space-y-2", children: users === null || users === void 0 ? void 0 : users.map(function (user) { return (_jsxs("div", { className: "flex items-center justify-between rounded-md border p-2", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(Avatar, { fallback: user.name, size: "sm" }), _jsxs("div", { children: [_jsx("div", { className: "text-sm font-medium", children: user.name || user.email }), _jsx("div", { className: "text-xs text-secondary", children: user.email })] })] }), _jsxs("div", { className: "flex items-center gap-1", children: [user.isExternal && _jsx("div", { className: "text-xs text-secondary", children: "External" }), user.isAuthor ? (_jsx("div", { className: "text-xs text-secondary", children: "Author" })) : (_jsx(Button, { variant: "ghost", size: "icon-sm", onClick: function () { return handleRemoveUser(user.id); }, disabled: removeUserMutation.isPending, children: removeUserMutation.isPending ? (_jsx("span", { className: "animate-spin", children: "..." })) : (_jsx(XIcon, { className: "size-4" })) }))] })] }, user.id)); }) })] })] })] })] }));
};
